// Full list of an Item's trips. A "trip" is one or more TaskPogoTrip legs that
// share a Trip Name (unnamed legs stand alone); this page groups the legs and
// shows one row per trip -- date range, name, and a distance summary. Rows open
// the trip-group page, which lists the individual legs (mirroring the old
// trips table). Add creates a stub leg and opens its detail page. Import JSON
// creates a whole new trip from an export file -- it lives here, not on the
// group page, because the result is a new row in this list.
//
// Below the list, the same trips on a month calendar (TripsCalendarSection).
// It reads its own legs and owns the page's ?month= / ?trips= query params --
// see that file's header.

import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useRootCrumbNavigate } from '../item/RootCrumb';
import Button from 'react-bootstrap/Button';
import { DataCard, useStatusMessage } from '../../components';
import * as Database from '../../utilities/Database.mjs';
import { urlForTrip, urlForTrips, urlForTripGroup } from '../../fleet-shared/Urls.mjs';
import { groupTrips, sortTripGroups, groupWithStoredLegs } from '../../fleet-shared/TripGroups.mjs';
import { tripWithResolvedEndpoints } from '../../fleet-shared/TripRoute.mjs';
import { parseTripExport } from '../../fleet-shared/TripIO.mjs';
import { readFileAsText } from '../../utilities/downloadFile.mjs';
import { ensurePlaces, placesFailedNotice } from '../../utilities/places.mjs';
import { useMyInfiniteScroll } from '../../components/MyInfiniteScroll';
import { CardHeader } from '../../components/CardHeader';
import { ItemBreadcrumbs } from '../item/ItemBreadcrumbs';
import TripGroupsTable from './TripGroupsTable.jsx';
import { usePlaceIndex } from '../../utilities/usePlaceIndex';
import ImportTripModal from './ImportTripModal';
import TripsCalendarSection from './TripsCalendarSection';

import { generateClient } from 'aws-amplify/api';
const client = generateClient();

const Trips = () => {
  const { itemId } = useParams();
  const navigate = useRootCrumbNavigate();

  const [item, setItem] = useState(null);

  // The gazetteer, for the group names and distance lines below.
  const placeIndex = usePlaceIndex();

  const [StatusMessage, {showText, showError, hideStatus}] = useStatusMessage();

  // Load the item for the breadcrumbs. Cheap and independent of the paginated
  // trip list below.
  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const loadedItem = await Database.retrieveItem(itemId);
        if (!mounted) return;
        setItem(loadedItem);
        // A prior failure's banner must not outlive the failure -- without
        // this, a transient error on mount stays red for the life of the page.
        hideStatus();
      } catch (err) {
        showError(err);
      }
    }
    load();
    return () => { mounted = false; };
  }, [itemId, showError, hideStatus]);

  // Group whatever legs have loaded so far and render one row per trip, OLDEST
  // first -- the order the Dates column reads in, and the reason the sort is
  // 'asc': that branch keys on startDate, the date each row leads with, where
  // 'desc' keys on throughDate (the trip's last day) and so puts rows whose
  // start dates don't march in step.
  //
  // The fetch direction below goes with it, and has to. Pages are APPENDED
  // (mergeEntriesById never re-sorts), so fetching DESC while rendering asc
  // would insert each new page ABOVE what the user is reading. Fetching ASC
  // also keeps the partial-group invariant, mirrored: legs arrive date-ASC, so
  // a group's earliest leg is seen first and startDate -- the sort key -- is
  // fixed the moment the group appears. Only the NEWEST visible group can be
  // partially loaded (its later legs sit on a not-yet-fetched page); it
  // completes itself as the user scrolls without moving.
  //
  // Grouped from DISPLAY copies of the legs: a group's from/to are flattened
  // out of its legs' columns, which are a save-time mirror of the route, so a
  // place renamed in /me/places is only reachable through the waypoint's
  // placeId in that route. `onDetails` puts the stored legs back before the
  // handover -- see groupWithStoredLegs.
  const renderChildren = (trips) => {
    const displayLegs = (trips ?? []).map((leg) => tripWithResolvedEndpoints(leg, placeIndex));
    const groups = sortTripGroups(groupTrips(displayLegs, { placeIndex }), 'asc');
    return (
      <TripGroupsTable
        groups={groups}
        placeIndex={placeIndex}
        onDetails={(group) => onDetails(groupWithStoredLegs(group, trips))}
      />
    );
  };

  // Go get the next page of trip legs.
  const fetchMoreEntries = useCallback(async (pageToken) => {
    const query = {
      query: Database.APITripsByItemId,
      variables: {
        itemId: itemId,
        sortDirection: 'ASC',
        ...(pageToken && { nextToken: pageToken })
      }
    };
    let results = await client.graphql(query);

    return {
      nextToken: results.data.tripsByItemId.nextToken,
      items: results.data.tripsByItemId.items
    };
  }, [itemId]);

  const endMessage = () => (
    <div className="text-center text-body-secondary py-3 small">
      — End of trips —
    </div>
  );

  const [trips, setTrips] = useState(null);

  const { Control } = useMyInfiniteScroll(
    renderChildren,
    fetchMoreEntries,
    trips,
    setTrips,
    endMessage
  );

  // Called when a new trip leg is added to the DB: edit it on its detail page.
  // No local-state insert -- this page unmounts on navigate and rebuilds its
  // list from the DB on return. `item` may still be null if Add beats the
  // breadcrumb fetch; Trip.jsx guards the handover state and refetches.
  function onAdded(newTrip) {
    navigate(urlForTrip(itemId, newTrip.id), { state: { item, trip: newTrip } });
  }

  async function handleAdd() {
    await Database.createTripWithUI(itemId, showText, showError, hideStatus, onAdded);
  }

  function onDetails(group) {
    navigate(urlForTripGroup(itemId, group.key), {
      state: { item, group },
    });
  }

  // Import flow: a validated file awaiting confirmation in ImportTripModal.
  const fileInputRef = useRef(null);
  const [pendingImport, setPendingImport] = useState(null);

  function pickImportFile() {
    fileInputRef.current?.click();
  }

  // Validate the picked file; a good one opens the confirm modal. Parse
  // errors land in the page StatusMessage (no modal is open yet).
  async function handleImportFile(e) {
    const file = e.target.files?.[0];
    // Reset so choosing the same file again still fires `change`.
    e.target.value = '';
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      setPendingImport(parseTripExport(text));
      hideStatus();
    } catch (err) {
      showError(err);
    }
  }

  // Confirmed in the modal: create the file's legs as a new trip and open
  // its group page. Errors propagate to the modal's inline display.
  //
  // The created legs travel along in router state, and not just to save the
  // group page a fetch: importTrips returns the CREATE mutation responses --
  // base-table rows with server-assigned ids -- whereas the group page reads
  // legs back off tripsByItemId, a GSI that can lag a write by a moment. Hand
  // over what we know exists, and mark it `legsJustWritten` so the group page
  // adds back anything that read misses (see its load effect).
  async function importTrip(parsed, onProgress) {
    // The gazetteer rows the file's routes refer to, first: create-only, so a
    // name this environment already has is left exactly as it is. The index it
    // returns binds the imported legs to rows that exist HERE rather than to
    // the ids of the environment that wrote the file. Never fatal -- the legs
    // are the user's data and the place rows are a bonus.
    const { failed, index } = await ensurePlaces(parsed.places, { onProgress });

    const result = await Database.importTrips(itemId, parsed, { onProgress, placeIndex: index });
    setPendingImport(null);
    const group = groupTrips(result.legs).find((g) => g.key === result.groupKey);
    navigate(urlForTripGroup(itemId, result.groupKey), {
      state: { item, group, legsJustWritten: true, notice: placesFailedNotice(failed) },
    });
  }

  // Show the trips in a table
  return (
    <div className='pb-2'>
      <ItemBreadcrumbs
        item={item}
        extras={[{ name: 'Trips', url: urlForTrips(itemId) }]}
      />

      <StatusMessage />

      { (trips && trips.length === 0) ? (
        <p className="text-center text-body-secondary">
          No trips yet. Add one to plan your next outing.
        </p>
      ) : null }

      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        className="d-none"
        aria-label="Import trip JSON file"
        onChange={handleImportFile}
      />

      {/* body is unconditional: the hook's error StatusMessage renders
          inside Control, so gating on `trips` (null until the first page
          lands) unmounted the error the moment a first fetch failed -- the
          list went silently empty. Control renders nothing while loading. */}
      <DataCard
        header={
          <CardHeader
            text='Trips'
            addTapped={handleAdd}
            extraActions={
              <Button variant="outline-secondary" onClick={pickImportFile}>
                Import JSON
              </Button>
            }
          />
        }
        body={<Control />}
      />

      {/* The same trips on a month grid, one scroll down. It does its OWN read
          of the legs rather than sharing the list's: the scroll above holds a
          prefix of them, and a bar spans a whole trip. The item and the
          gazetteer it takes from here, though -- one snapshot of the names, so
          the table and the grid can't spell a place two ways. */}
      <TripsCalendarSection itemId={itemId} item={item} placeIndex={placeIndex} />

      <ImportTripModal
        parsed={pendingImport}
        onHide={() => setPendingImport(null)}
        onImport={importTrip}
      />
    </div>
  );
}

export default Trips;
